import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGarden } from '../context/GardenContext'
import { type PlantFilters, usePlantCatalog } from '../context/PlantCatalogContext'
import FilterPanel from '../components/FilterPanel'
import PlantCard from '../components/PlantCard'

const DEFAULT_FILTERS = {
  roles: [] as string[],
  sunlight: [] as string[],
  water: [] as string[],
  colors: [] as string[],
  pestResistance: [] as string[],
  onlyNative: false,
  onlySafePets: false,
  onlySafeChildren: false,
  onlyPerennial: false,
  windTolerance: [] as string[],
  searchText: '',
}

type Filters = typeof DEFAULT_FILTERS

const CROWDING_GUIDANCE: Record<string, number> = { small: 1, medium: 3, large: 5, xlarge: 7 }

export default function PlantBrowser() {
  const { state, dispatch } = useGarden()
  const { plants, filterPlants, isLoading, error, source } = usePlantCatalog()
  const navigate = useNavigate()
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS)
  const [addingPlantId, setAddingPlantId] = useState<string | null>(null)
  const [showMobileFilters, setShowMobileFilters] = useState(false)

  const effectiveFilters = useMemo<PlantFilters>(() => ({
    ...filters,
    region: state.region,
    sunlight: filters.sunlight.length ? filters.sunlight : state.preferredSunlight ? [state.preferredSunlight] : [],
  }), [filters, state.preferredSunlight, state.region])

  const filteredPlants = useMemo(
    () => filterPlants(null, effectiveFilters),
    [effectiveFilters, filterPlants]
  )

  function handleSave(id: string) {
    dispatch({ type: 'TOGGLE_SAVED_PLANT', plantId: id })
  }

  function handleAddToContainer(plantId: string) {
    if (state.containers.length === 0) {
      navigate('/setup')
      return
    }
    setAddingPlantId(plantId)
  }

  function confirmAddToContainer(containerId: string) {
    if (!addingPlantId) return
    dispatch({ type: 'ADD_PLANT_TO_CONTAINER', containerId, plantId: addingPlantId })
    setAddingPlantId(null)
  }

  const regionLabel = state.region === 'OR' ? 'Oregon' : state.region === 'WA' ? 'Washington' : null

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 page-enter">
      <div className="text-center mb-6 space-y-2">
        <h1 className="font-pixel text-ac-brown text-base">Plant Library</h1>
        {!regionLabel ? (
          <p className="font-body text-amber-800 bg-amber-50 rounded-lg px-4 py-2 inline-block text-sm">
            Choose Oregon or Washington on the <button className="underline" onClick={() => navigate('/')}>first screen</button> to prioritize regional native plants.
          </p>
        ) : (
          <p className="font-body text-ac-brown-light text-sm">
            Prioritizing <strong>{regionLabel}</strong> native options
            {state.preferredSunlight ? ` for ${state.preferredSunlight.replace('-', ' ')}` : ''} · {filteredPlants.length} matches
          </p>
        )}
        <p className="font-body text-xs text-ac-brown-light">
          {source === 'supabase'
            ? 'Using source-reviewed library data. Rows marked needs_review are hidden.'
            : 'Using local starter data while Supabase library data is unavailable; treat claims as demo-only.'}
          {isLoading ? ' Loading library...' : ''}
        </p>
        {error && (
          <p className="font-body text-xs text-amber-800 bg-amber-50 rounded-lg px-3 py-2 inline-block">
            Showing local plant data — remote library temporarily unavailable.
          </p>
        )}
      </div>

      <div className="flex gap-6 items-start">
        <div className="w-64 shrink-0 sticky top-4 hidden lg:block">
          <FilterPanel
            filters={filters}
            onChange={setFilters}
            totalShown={filteredPlants.length}
            totalAll={plants.length}
          />
        </div>

        <div className="lg:hidden w-full mb-4">
          <div className="ac-card">
            <button
              className="w-full flex items-center justify-between font-body font-bold text-ac-brown text-sm"
              onClick={() => setShowMobileFilters(f => !f)}
            >
              <span>Filters · {filteredPlants.length} plants shown</span>
              <span className={`transition-transform duration-200 text-base ml-2 ${showMobileFilters ? 'rotate-180' : ''}`}>v</span>
            </button>
            {showMobileFilters && (
              <div className="mt-3">
                <FilterPanel
                  filters={filters}
                  onChange={setFilters}
                  totalShown={filteredPlants.length}
                  totalAll={plants.length}
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex-1">
          {filteredPlants.length === 0 ? (
            <div className="text-center py-16 space-y-3">
              <p className="font-body font-bold text-ac-brown">No plants match your filters</p>
              <p className="font-body text-sm text-ac-brown-light">Try removing a filter or report a missing PNW plant from the final review screen.</p>
              <button className="ac-btn-outline" onClick={() => setFilters(DEFAULT_FILTERS)}>
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
              {filteredPlants.map(plant => (
                <PlantCard
                  key={plant.id}
                  plant={plant}
                  isSaved={state.savedPlantIds.includes(plant.id)}
                  onSave={handleSave}
                  onAddToContainer={handleAddToContainer}
                />
              ))}
            </div>
          )}

          {state.savedPlantIds.length > 0 && (
            <div className="mt-6 ac-card bg-ac-yellow/30 border-ac-yellow">
              <p className="font-body font-bold text-ac-brown text-sm">
                {state.savedPlantIds.length} plant{state.savedPlantIds.length !== 1 ? 's' : ''} saved for nursery review
              </p>
              <p className="font-body text-xs text-ac-brown-light mt-1">
                Saved plants stay in this browser and appear on the shopping list.
              </p>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button className="ac-btn-green" onClick={() => navigate('/design')}>
              Design containers
            </button>
          </div>
        </div>
      </div>

      {addingPlantId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ac-brown/50" onClick={() => setAddingPlantId(null)}>
          <div className="ac-dialog p-6 max-w-sm w-full space-y-4" onClick={e => e.stopPropagation()}>
            <h2 className="font-pixel text-ac-brown text-sm">Add to which container?</h2>
            <div className="space-y-2">
              {state.containers.map(container => {
                const guidanceLimit = CROWDING_GUIDANCE[container.size] ?? 3
                const mayBeCrowded = container.plantIds.length >= guidanceLimit
                return (
                  <button
                    key={container.id}
                    onClick={() => confirmAddToContainer(container.id)}
                    className="w-full text-left p-3 rounded-xl border-2 border-ac-brown bg-ac-cream hover:bg-ac-yellow/30 font-body transition-all"
                  >
                    <p className="font-bold text-sm">{container.nickname}</p>
                    <p className="text-xs capitalize text-ac-brown-light">
                      {container.size} · {container.sunExposure.replace('-', ' ')} · {container.plantIds.length} plant{container.plantIds.length !== 1 ? 's' : ''}
                      {mayBeCrowded ? ' · crowding risk' : ''}
                    </p>
                  </button>
                )
              })}
            </div>
            <div className="flex gap-2">
              <button className="ac-btn-outline flex-1" onClick={() => setAddingPlantId(null)}>Cancel</button>
              <button className="ac-btn-yellow flex-1" onClick={() => { setAddingPlantId(null); navigate('/setup') }}>
                New container
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
